import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["item"]

  connect() {
    this.boundScheduleLayout = () => this.scheduleLayout()
    this.boundAfterRender = () => {
      requestAnimationFrame(() => this.scheduleLayout())
    }

    this.resizeObserver = new ResizeObserver(this.boundScheduleLayout)
    this.resizeObserver.observe(this.element)
    this.observeItems()

    window.addEventListener("resize", this.boundScheduleLayout)
    this.element.addEventListener("resize", this.boundScheduleLayout)
    document.addEventListener("turbo:frame-load", this.boundAfterRender, true)
    document.addEventListener("turbo:render", this.boundAfterRender)

    // Right away, not on the next animation frame: Turbo inserts pages and frames from inside
    // a requestAnimationFrame callback, so "the next frame" came after a first paint — one
    // frame with every card stacked at the top (1px rows, no spans yet). That frame was the
    // flicker seen when a page or its skeleton arrived.
    this.layout()
  }

  disconnect() {
    this.resizeObserver?.disconnect()
    this.resizeObserver = null
    cancelAnimationFrame(this.layoutFrame)
    window.removeEventListener("resize", this.boundScheduleLayout)
    this.element.removeEventListener("resize", this.boundScheduleLayout)
    document.removeEventListener("turbo:frame-load", this.boundAfterRender, true)
    document.removeEventListener("turbo:render", this.boundAfterRender)
  }

  itemTargetConnected(item) {
    // Stimulus connects the initial targets before connect(), which lays them out itself.
    if (!this.resizeObserver) return

    this.resizeObserver.observe(item)
    this.layoutBeforePaint()
  }

  itemTargetDisconnected(item) {
    this.resizeObserver?.unobserve(item)
  }

  observeItems() {
    this.itemTargets.forEach((item) => this.resizeObserver.observe(item))
  }

  scheduleLayout() {
    cancelAnimationFrame(this.layoutFrame)
    this.layoutFrame = requestAnimationFrame(() => this.layout())
  }

  // A card added later (turbo stream, lazy frame) must not get a frame without its span either.
  // A microtask still runs before the paint, and batches the cards of one mutation.
  layoutBeforePaint() {
    if (this.layoutQueued) return

    this.layoutQueued = true
    queueMicrotask(() => {
      this.layoutQueued = false
      if (this.resizeObserver) this.layout()
    })
  }

  layout() {
    const styles = getComputedStyle(this.element)
    const items = this.itemTargets

    if (styles.gridTemplateColumns.split(" ").length < 2) {
      items.forEach((item) => item.style.removeProperty("grid-row-end"))
      return
    }

    const rowHeight = Number.parseFloat(styles.gridAutoRows) || 1

    // Every card is measured before any span is written: interleaving the two forced a new
    // layout per card. A card's height does not depend on its span (`align-items: start`).
    const spans = items.map((item) => {
      // The card's own bottom margin is the vertical gutter, so it must be part
      // of the span for vertical and horizontal spacing to match.
      const margin = Number.parseFloat(getComputedStyle(item).marginBottom) || 0
      return `span ${Math.ceil((item.getBoundingClientRect().height + margin) / rowHeight)}`
    })

    items.forEach((item, index) => {
      if (item.style.gridRowEnd !== spans[index]) item.style.gridRowEnd = spans[index]
    })
  }
}
